import { useEffect, useState, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { InputGroupText, UncontrolledTooltip } from 'reactstrap';
import Icon from 'utility/icomoon';
import MultiSelectInput from '../MultiSelectInput';

import * as authSelectors from 'views/../redux/reducers/auth';
import SlidingPaneAddEditContact from 'views/formComponents/SlidingPaneAddEditContact';
import * as contactActions from 'views/screens/Contacts/actions';
import * as ContactSelectors from 'views/screens/Contacts/reducers';
import { useGetOperatorContacts } from 'views/screens/Operators/hooks/useGetOperatorContacts';
import { useGetContacts } from 'views/screens/Organizations/hooks/useGetContacts';
import { format } from 'path';

interface Props {
  id: any;
  name: any;
  control: any;
  shouldUnregister?: any;
  defaultContacts?: any;
  extraContacts?: any;
  label?: any;
  isInModal?: boolean;
  initialFetch?: boolean;
  required?: boolean;
  contactTypeId?: number;
  isMultiple?: boolean;
  isClearable?: boolean;
  canCreate?: boolean;
  setContactsIn?: any;
  contactsIn?: any;
  setNewContact?: (value) => void;
  closeSlidingPaneAddEditContact?: () => void;
  prependcomponentFun?: () => void;
  appendcomponentFun?: () => void;
  operatorId?: number;
  organizationId?: number;
  defaultJobTitle?: string;
  watch?: any;
  reset?: any;
}

const ContactsDropdown = (props: Props) => {
  const store = useSelector((state) => state);
  const userInSession = authSelectors.getUser(store);

  const {
    id,
    name,
    shouldUnregister,
    defaultContacts,
    extraContacts = [],
    label,
    isInModal,
    isMultiple = true,
    isClearable = false,
    initialFetch = true,
    canCreate = true,
    required,
    control,
    contactTypeId = 1,
    setContactsIn,
    contactsIn,
    setNewContact = null,
    closeSlidingPaneAddEditContact = undefined,
    prependcomponentFun = undefined,
    appendcomponentFun = undefined,
    operatorId = authSelectors.getAuthUserOperatorId(store),
    organizationId = authSelectors.getAuthUserOrganizationId(store),
    defaultJobTitle = '',
    watch = undefined,
    reset = undefined
  } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const [canCreateContact, setCanCreateContact] = useState(true);

  const [selectedContacts, changeSelectedContacts] = useState(
    Array.isArray(defaultContacts)
      ? defaultContacts
      : typeof defaultContacts === 'string'
      ? defaultContacts.split(',').filter(Boolean)
      : []
  );

  const controllerDefaultValue = (() => {
    if (!defaultContacts) return '';
    if (typeof defaultContacts === 'string') return defaultContacts; // ya es CSV
    if (Array.isArray(defaultContacts)) return defaultContacts.join(',');
    return '';
  })();

  const [addEditContactSlideOpen, openAddEditContactSlide] = useState(false);
  const [newContact, changeNewContact] = useState<any>(null);
  // Selección optimista
  const [optimisticOptions, setOptimisticOptions] = useState<any[]>([]);
  const fieldOnChangeRef = useRef<((val: any) => void) | null>(null);
  const fieldValueRef = useRef<string | null>('');

  const selectAndEditContact = (contactId) => {
    dispatch(contactActions.selectContact(contactId));
    openAddEditContactSlide(true);
  };

  const selectedContactOnCreate = useSelector(ContactSelectors.getSelectedContact);
  const contacts = useSelector(ContactSelectors.getContactsList);
  const userContactsEmails = contacts.map((c) => c.email);

  const { operatorPublicContacts, areFetchingOperatorContacts } = (() => {
    const { operatorContacts, isFetchingOperatorContacts } = useGetOperatorContacts(operatorId);
    return {
      areFetchingOperatorContacts: isFetchingOperatorContacts,
      operatorPublicContacts: operatorContacts.map((c) => ({ ...c, contactTypeId: 2 }))
    };
  })();

  const { organizationPublicContacts, areFetchingOrganizationContacts } = (() => {
    const { contacts, isFetching } = useGetContacts(organizationId);
    return {
      organizationPublicContacts: contacts.map((c) => ({ ...c, contactTypeId: 3 })),
      areFetchingOrganizationContacts: isFetching
    };
  })();

  const formatContactsForDropdown = (contact) => {
    if (contact)
      return {
        ...contact,
        id: contact.contactId,
        value: contactTypeId === 1 ? contact.email : contact.contactId,
        label:
          contact.contactName !== contact.email
            ? `${contact.contactName} <${contact.email}>`
            : contact.contactName,
        edit: selectAndEditContact,
        identifierIcon: (
          <>
            <UncontrolledTooltip
              placement={'top'}
              target={`contactInsideDropdown_${contact.contactId}_${id}`}
            >
              {intl.formatMessage({
                id:
                  contact.contactTypeId === 2
                    ? 'contacts.operatorContact'
                    : contact.contactTypeId === 3
                    ? 'contacts.organizationContact'
                    : 'contacts.userContact'
              })}
            </UncontrolledTooltip>
            <Icon
              icon={
                contact.contactTypeId === 3
                  ? 'Organization'
                  : contact.contactTypeId === 2
                  ? 'Operator'
                  : 'noun_User_2905483'
              }
              size={15}
              id={`contactInsideDropdown_${contact.contactId}_${id}`}
            />
          </>
        )
      };
    return {};
  };

  const filterContactByAcces = (
    userContacts: Array<any>,
    operatorContacts: Array<any>,
    OrganizationContacts: Array<any>
  ): Array<any> => {
    const usedEmails: Array<string> = userContacts.map((c) => c.email);
    const filteredContacts: Array<any> = [...userContacts];

    const addNotIncludedContacts = (contact: any) => {
      if (usedEmails.includes(contact.email)) return;
      usedEmails.push(contact.email);
      filteredContacts.push(contact);
    };

    if (['9', '11', '13'].indexOf(userInSession?.accessLevelId) >= 0) {
      operatorContacts?.forEach(addNotIncludedContacts);
    }

    if (['7', '9', '11', '13'].indexOf(userInSession?.accessLevelId)) {
      OrganizationContacts?.forEach(addNotIncludedContacts);
    }

    return filteredContacts?.map(formatContactsForDropdown);
  };

  const contactsToShow: Array<any> = filterContactByAcces(
    contacts,
    operatorPublicContacts,
    organizationPublicContacts
  );

  const contactsToAdd =
    contactTypeId === 1
      ? defaultContacts !== undefined && defaultContacts !== null && defaultContacts !== ''
        ? defaultContacts
            .split(',')
            .filter((c) => !userContactsEmails.includes(c))
            .filter(
              (defaultContact) =>
                contactsToShow
                  .concat(extraContacts)
                  .filter((contact) => contact.id === defaultContact).length === 0
            )
            .map((contactToAdd) => ({
              ...(contactTypeId === 1 ? {} : contactToAdd),
              id: contactTypeId === 1 ? contactToAdd : contactToAdd.contactId,
              value: contactTypeId === 1 ? contactToAdd : contactToAdd.contactId,
              label:
                contactTypeId === 1
                  ? contactToAdd
                  : contactToAdd.contactName !== contactToAdd.email
                  ? `${contactToAdd.contactName} <${contactToAdd.email}>`
                  : contactToAdd.contactName
            }))
        : []
      : [];

  const [resetValue, setResetValue] = useState(Date.now());
  const [selectedContact, setSelectedContact] = useState<any>(
    selectedContacts !== undefined &&
      selectedContacts !== null &&
      selectedContacts.length > 0 &&
      !isMultiple
      ? contactsToShow
          .concat(extraContacts)
          .concat(contactsToAdd)
          .filter((contact) => selectedContacts.includes(`${contact.contactId}`))[0]
      : null
  );

  useEffect(() => {
    changeSelectedContacts(
      Array.isArray(defaultContacts)
        ? defaultContacts
        : typeof defaultContacts === 'string'
        ? defaultContacts.split(',').filter(Boolean)
        : []
    );
    setResetValue(Date.now());
  }, [defaultContacts]);

  // Realizar el fetch inicial de contactos
  useEffect(() => {
    if (selectedContacts || initialFetch) {
      dispatch(contactActions.startFetchingContacts(null, null, null));
    }
  }, [selectedContacts, initialFetch, dispatch]);

  // Limpiar opciones optimistas cuando ya existen en la lista real
  useEffect(() => {
    if (!optimisticOptions.length) return;
    setOptimisticOptions((prev) =>
      prev.filter(
        (opt) =>
          !contacts.some((c) => {
            const realValue = contactTypeId === 1 ? c.email : c.contactId;
            return realValue === opt.value;
          })
      )
    );
  }, [contacts, contactTypeId, optimisticOptions]);

  // Función para manejar la creación de un nuevo contacto (optimista)
  const onCreate = (event) => {
    const raw = event[event.length - 1];
    const inputValue = (raw?.value || '').trim();
    if (!inputValue) return;
    const localFullOptions = contactsToShow
      .concat(extraContacts)
      .concat(contactsToAdd)
      .concat(optimisticOptions);
    const exists = localFullOptions.some(
      (opt) => opt?.value === inputValue || opt?.label === inputValue
    );
    if (exists) {
      setCanCreateContact(false);
      setTimeout(() => setCanCreateContact(true), 1000);
      return;
    }
    const newContactData = {
      contactName: inputValue,
      email: inputValue,
      contactId: uuidv4(),
      contactTypeId
    };
    const formatted = formatContactsForDropdown(newContactData);
    if (formatted) {
      setOptimisticOptions((prev) => [...prev, formatted]);
      // Para multi no necesitamos selectedContacts para selección visual, solo mantener coherencia
      if (!isMultiple) {
        changeSelectedContacts((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
            return arr.includes(formatted.id) ? arr : [...arr, formatted.id];
        });
      }
    }
    changeNewContact(newContactData);
    dispatch(contactActions.startAddingContact(newContactData));
    setTimeout(() => {
      dispatch(contactActions.startFetchingContacts(null, null, null));
    }, 1000);
  };

  // Función para manejar la edición de un contacto existente
  const onEdit = (editedContactData) => {
    // Actualizar el contacto temporalmente en el estado local
    changeSelectedContacts((prevContacts) =>
      prevContacts.map((contact) =>
        contact.contactId === editedContactData.contactId ? editedContactData : contact
      )
    );

    // Llamar a la acción para editar el contacto
    dispatch(contactActions.startEditingContact(editedContactData));

    // Luego realizar un nuevo fetch de contactos para actualizar la lista completa
    setTimeout(() => {
      dispatch(contactActions.startFetchingContacts(null, null, null));
    }, 1000); // Un pequeño retraso para asegurar que el contacto se haya actualizado en el backend
  };

  // Asegura que todas las values presentes en el CSV tengan una opción renderizable
  const ensureAllSelectedOptions = (opts: any[], fieldValue: any) => {
    if (typeof fieldValue !== 'string' || !fieldValue) return opts;
    const set = new Set(opts.map((o) => (isNaN(o.value) ? o.value : o.value.toString())));
    const toAdd: any[] = [];
    fieldValue.split(',').filter(Boolean).forEach((val) => {
      if (!set.has(val)) {
        toAdd.push({ id: val, value: val, label: val });
      }
    });
    return toAdd.length ? opts.concat(toAdd) : opts;
  };

  return (
    <>
      <Controller
        control={control}
        shouldUnregister={shouldUnregister}
        name={name}
        defaultValue={controllerDefaultValue}
        rules={{ required: required }}
        render={({ field, fieldState }) => {
          let fullOptions = [...contactsToShow, ...extraContacts, ...contactsToAdd, ...optimisticOptions].reduce(
            (acc: any[], opt) => {
              if (!acc.some((o) => o.value === opt.value)) acc.push(opt);
              return acc;
            },
            []
          );
          fullOptions = ensureAllSelectedOptions(fullOptions, field.value);
          fieldOnChangeRef.current = field.onChange;
          fieldValueRef.current = field.value;
          return (
            <MultiSelectInput
              field={field}
              fieldstate={fieldState}
              id={id}
              required={required}
              isClearable={isClearable}
              isMultiple={isMultiple}
              isInModal={isInModal}
              key={resetValue}
              prependcomponent={
                isMultiple ? undefined : (
                  <>
                    <UncontrolledTooltip placement={'top'} target={'editContactDropdownButton' + id}>
                      {intl.formatMessage({ id: 'contacts.editContact' })}
                    </UncontrolledTooltip>
                    <InputGroupText
                      id={'editContactDropdownButton' + id}
                      style={{ cursor: 'pointer' }}
                      className={'m-0'}
                      onClick={() => {
                        if (
                          selectedContacts !== undefined &&
                          selectedContacts !== null &&
                          selectedContacts.length > 0
                        ) {
                          openAddEditContactSlide(true);
                          if (prependcomponentFun) prependcomponentFun();
                          setSelectedContact(
                            selectedContacts !== undefined &&
                              selectedContacts !== null &&
                              selectedContacts.length > 0 &&
                              !isMultiple
                              ? contactsToShow
                                  .concat(extraContacts)
                                  .concat(contactsToAdd)
                                  .filter((contact) =>
                                    selectedContacts.includes(`${contact.contactId}`)
                                  )[0]
                              : null
                          );
                        }
                      }}
                    >
                      <Icon icon={'Edit2'} />
                    </InputGroupText>
                  </>
                )
              }
              appendcomponent={
                isMultiple ? undefined : (
                  <>
                    <UncontrolledTooltip placement={'top'} target={'addContactDropdownButton' + id}>
                      {intl.formatMessage({ id: 'contacts.addContact' })}
                    </UncontrolledTooltip>
                    <InputGroupText
                      id={'addContactDropdownButton' + id}
                      style={{ cursor: 'pointer' }}
                      className={'m-0'}
                      onClick={() => {
                        if (appendcomponentFun) appendcomponentFun();
                        openAddEditContactSlide(true);
                        setSelectedContact(null);
                      }}
                    >
                      <Icon icon={'Plus'} />
                    </InputGroupText>
                  </>
                )
              }
              onCreate={onCreate}
              onSelect={(event) => {
                if (isMultiple) {
                  // No dependemos de selectedContacts para render; pero mantenemos por consistencia mínima
                  const selectedIds = event ? event.map((item) => item.id || item.contactId || item.value) : [];
                  changeSelectedContacts(selectedIds);
                } else {
                  const selectedId = event && (event.id || event.contactId || event.value) ? [event.id || event.contactId || event.value] : [];
                  changeSelectedContacts(selectedId);
                }
              }}
              options={fullOptions}
              label={
                label ||
                intl.formatMessage({
                  id: 'contacts.contacts'
                })
              }
              placeholder={intl.formatMessage({ id: 'contacts.placeholder' })}
              canCreate={isMultiple && canCreate && canCreateContact}
            />
          );
        }}
      />
      <SlidingPaneAddEditContact
        operatorContact={contactTypeId === 2}
        organizationContact={contactTypeId === 3}
        userContact={contactTypeId === 1}
        contactsIn={contactsIn}
        setContactsIn={setContactsIn}
        selectedContact={isMultiple ? undefined : selectedContact}
        isOpenSlidingPane={addEditContactSlideOpen}
        closeSlidingPaneAddEditContact={() => {
          openAddEditContactSlide(false);
          if (closeSlidingPaneAddEditContact) closeSlidingPaneAddEditContact();
        }}
        setNewContact={changeNewContact}
        defaultJobTitle={defaultJobTitle}
        canCreate={
          ( canCreate && contactTypeId !== 1) || (contactTypeId === 1 && canCreate)
        }
        operatorId={operatorId}
        organizationId={organizationId}
        isSelectedOnCreate
        isEditingFromMulti={isMultiple && contactTypeId === 1}
        // onSave={onEdit} // Comentado temporalmente - propiedad no existe en el componente
      />
    </>
  );
};

ContactsDropdown.defaultProps = {
  isInModal: false
};

export default ContactsDropdown;
